import { and, eq, sql } from "drizzle-orm";
import type { Span } from "@opentelemetry/api";

import type { WorkspaceMembership, WsItem } from "@storyboard/web-client";
import { createLogger } from "@/logger";

const log = createLogger("warmup");

import { client } from "@/config";
import { db } from "@/db/db";
import { feed, item } from "@/db/schema";
import type { Feed } from "@/db/types";
import { downloadOldestFeedItemEvents } from "@/models/actions/bootstrap";
import { emitTableUpdates } from "@/models/eventing/TableEventManager";
import { bulkUpsertWsAudioEncodings } from "@/models/repositories/AudioEncoding";
import { bulkUpsertCallRecord } from "@/models/repositories/CallRecord";
import { bulkUpsertDisplayArtifact } from "@/models/repositories/DisplayArtifact";
import { topFeedsByActivityQuery } from "@/models/repositories/Feed";
import { bulkUpsertFile } from "@/models/repositories/File";
import {
  bulkUpsertItemAndCalculateMembership,
  markItemsContentLoaded,
  unreadFeedIdsQuery,
} from "@/models/repositories/Item";
import { bulkUpsertLink } from "@/models/repositories/Link";
import { bulkUpsertWsTranscriptions } from "@/models/repositories/Transcription";
import { actions } from "@/models/TelemetryProvider";

const DEFAULT_WARMUP_FEED_LIMIT = 50;
const WARMUP_ITEMS_PER_FEED = 10;

type WarmupStats = {
  feedsWarmedUp: number;
  itemsLoaded: number;
  contentDownloaded: number;
  durationMs: number;
};

type FeedsToWarmup = {
  feedIds: string[];
  unreadCount: number;
  activityCount: number;
  totalUnique: number;
  alreadyLoaded: number;
};

async function determineFeedsToWarmup({
  workspaceId,
  workspaceMembershipId,
  warmupFeedLimit,
}: {
  workspaceId: string;
  workspaceMembershipId: string;
  warmupFeedLimit: number;
}): Promise<FeedsToWarmup> {
  const feedIds = new Set<string>();
  let unreadCount = 0;
  let activityCount = 0;

  if (warmupFeedLimit <= 0) {
    return { feedIds: [], unreadCount: 0, activityCount: 0, totalUnique: 0, alreadyLoaded: 0 };
  }

  const unreadFeeds = await unreadFeedIdsQuery({
    myCurrentWorkspaceMembershipId: workspaceMembershipId,
    requireNotificationPreference: false,
    limit: warmupFeedLimit,
  }).execute();

  unreadCount = unreadFeeds.length;

  for (const f of unreadFeeds) {
    feedIds.add(f.feedId);
  }

  log.info("[WARMUP] Found feeds with unreads", {
    count: unreadFeeds.length,
    limit: warmupFeedLimit,
    feedIds: Array.from(feedIds),
  });

  const remainingSlots = warmupFeedLimit - feedIds.size;

  if (remainingSlots > 0) {
    const topFeeds = await topFeedsByActivityQuery({
      workspaceId,
      myCurrentWorkspaceMembershipId: workspaceMembershipId,
      limit: warmupFeedLimit,
      requireNotificationPreference: false,
    }).execute();

    for (const f of topFeeds) {
      if (feedIds.size >= warmupFeedLimit) break;
      if (!feedIds.has(f.id)) {
        feedIds.add(f.id);
        activityCount++;
      }
    }

    log.info("[WARMUP] Filled remaining slots from top feeds by activity", {
      remainingSlots,
      activityFeedsAdded: activityCount,
      totalFeeds: feedIds.size,
    });
  }

  const finalFeedIds = Array.from(feedIds);

  // Filter out feeds that already have their last page loaded
  let alreadyLoadedCount = 0;
  let feedsToWarmup: string[] = finalFeedIds;

  if (finalFeedIds.length > 0) {
    const feedsWithLastPageLoaded = await db.query.feed
      .findMany({
        where: (feedTable, { inArray, eq, and }) =>
          and(inArray(feedTable.id, finalFeedIds), eq(feedTable.loadedLastPage, true)),
        columns: { id: true },
      })
      .execute();

    const alreadyLoadedIds = new Set(feedsWithLastPageLoaded.map((f) => f.id));
    alreadyLoadedCount = alreadyLoadedIds.size;
    feedsToWarmup = finalFeedIds.filter((id) => !alreadyLoadedIds.has(id));

    log.info("[WARMUP] Filtered out already loaded feeds", {
      totalCandidates: finalFeedIds.length,
      alreadyLoaded: alreadyLoadedIds.size,
      needsWarmup: feedsToWarmup.length,
    });
  }

  return {
    feedIds: feedsToWarmup,
    unreadCount,
    activityCount,
    totalUnique: finalFeedIds.length,
    alreadyLoaded: alreadyLoadedCount,
  };
}

async function fetchFeedItemsFromAPI({
  feedId,
  feedName,
  workspaceId,
  trackAction,
  finishAction,
}: {
  feedId: string;
  feedName: string;
  workspaceId: string;
  trackAction?: (props: { action: string; target?: string }, customAttributes?: Record<string, unknown>) => Span;
  finishAction?: (props: { action: string; target?: string }, customAttributes?: Record<string, unknown>) => void;
}): Promise<{ feedId: string; items: WsItem[]; needsLastPageFlag: boolean }> {
  // Track individual feed fetch
  if (trackAction) {
    trackAction(actions.warmupFetchItems(feedId, feedName), {
      feedId,
      feedName,
    });
  }

  try {
    // Fetch most recent items (descending order, newest first) - same as when user clicks on feed
    const result = await client.bootstrapWorkspaceFeedItemsPaginated({
      workspaceId,
      feedId,
      pageSize: WARMUP_ITEMS_PER_FEED,
      sortAscending: false,
      useCreatedAt: true,
    });

    const itemCount = result?.items?.length || 0;
    const needsLastPageFlag = itemCount < WARMUP_ITEMS_PER_FEED;

    if (finishAction) {
      finishAction(actions.warmupFetchItems(feedId, feedName), {
        feedId,
        feedName,
        itemCount,
        needsLastPageFlag,
      });
    }

    return {
      feedId,
      items: result?.items || [],
      needsLastPageFlag,
    };
  } catch (error) {
    log.warn("[WARMUP] Failed to fetch items for feed", { feedId, feedName, error });

    if (finishAction) {
      finishAction(actions.warmupFetchItems(feedId, feedName), {
        feedId,
        feedName,
        error: String(error),
      });
    }

    return {
      feedId,
      items: [],
      needsLastPageFlag: false,
    };
  }
}

export async function warmupFeedItems({
  workspaceId,
  membership,
  trackAction,
  finishAction,
  warmupFeedLimit = DEFAULT_WARMUP_FEED_LIMIT,
}: {
  workspaceId: string;
  membership: WorkspaceMembership;
  trackAction?: (props: { action: string; target?: string }, customAttributes?: Record<string, unknown>) => Span;
  finishAction?: (props: { action: string; target?: string }, customAttributes?: Record<string, unknown>) => void;
  warmupFeedLimit?: number;
}): Promise<WarmupStats> {
  const startTime = Date.now();

  log.info("[WARMUP] Starting feed warmup", {
    workspaceId,
    membershipId: membership.id,
    warmupFeedLimit,
    warmupItemsPerFeed: WARMUP_ITEMS_PER_FEED,
  });

  const stats: WarmupStats = {
    feedsWarmedUp: 0,
    itemsLoaded: 0,
    contentDownloaded: 0,
    durationMs: 0,
  };

  // Start overall telemetry span
  if (trackAction) {
    trackAction(actions.warmupFull(), {
      workspaceId,
      membershipId: membership.id,
      warmupFeedLimit,
      warmupItemsPerFeed: WARMUP_ITEMS_PER_FEED,
    });
  }

  try {
    // Track calculation phase
    if (trackAction) {
      trackAction(actions.warmupCalculate(), {
        workspaceId,
        membershipId: membership.id,
      });
    }

    // Determine which feeds to warmup
    const feedsResult = await determineFeedsToWarmup({
      workspaceId,
      workspaceMembershipId: membership.id,
      warmupFeedLimit,
    });

    const { feedIds, unreadCount, activityCount, totalUnique, alreadyLoaded } = feedsResult;

    // Finish calculation phase
    if (finishAction) {
      finishAction(actions.warmupCalculate(), {
        totalFeeds: feedIds.length,
        unreadCount,
        activityCount,
        totalUnique,
        alreadyLoaded,
      });
    }

    if (feedIds.length === 0) {
      log.info("[WARMUP] No feeds to warmup");
      stats.durationMs = Date.now() - startTime;

      if (finishAction) {
        finishAction(actions.warmupFull(), stats);
      }

      return stats;
    }

    // Fetch feed names for better logging
    const feedRecords = await db.query.feed
      .findMany({
        where: (feedTable, { inArray }) => inArray(feedTable.id, feedIds),
        columns: { id: true, title: true },
      })
      .execute();
    const feedNameMap = new Map(feedRecords.map((f) => [f.id, f.title || f.id]));

    log.info("[WARMUP] Fetching items for all feeds in parallel", {
      feedCount: feedIds.length,
      feeds: feedIds.map((id) => ({ id, name: feedNameMap.get(id) })),
    });

    // Fetch all feed items in parallel (API calls only)
    // Each feed will track its own warmup.fetch_items action
    const feedResults = await Promise.all(
      feedIds.map((feedId) =>
        fetchFeedItemsFromAPI({
          feedId,
          feedName: feedNameMap.get(feedId) || feedId,
          workspaceId,
          trackAction,
          finishAction,
        }),
      ),
    );

    // Collect all items and feed flags to update
    const allItems: WsItem[] = [];
    const feedsNeedingLastPageFlag: string[] = [];
    const feedItemCounts: Record<string, { name: string; count: number }> = {};

    for (const result of feedResults) {
      const feedName = feedNameMap.get(result.feedId) || result.feedId;
      feedItemCounts[result.feedId] = { name: feedName, count: result.items.length };

      if (result.items.length > 0) {
        allItems.push(...result.items);
        stats.feedsWarmedUp += 1;
        stats.itemsLoaded += result.items.length;

        log.info(`[WARMUP] Feed "${feedName}" (${result.feedId}) loaded ${result.items.length} items`);
      } else {
        log.info(`[WARMUP] Feed "${feedName}" (${result.feedId}) had no items to load`);
      }

      if (result.needsLastPageFlag) {
        feedsNeedingLastPageFlag.push(result.feedId);
      }
    }

    log.info("[WARMUP] Finished fetching all feed items", {
      totalFeeds: feedIds.length,
      feedsWithItems: stats.feedsWarmedUp,
      totalItems: allItems.length,
      feedItemCounts,
    });

    // Batch DB operation: Insert all items at once (defer emit)
    if (allItems.length > 0) {
      log.info("[WARMUP] Bulk upserting all items", {
        itemCount: allItems.length,
      });
      await bulkUpsertItemAndCalculateMembership(allItems, { deferEmit: true });
    }

    // Batch DB operation: Update all feed flags at once
    let feedFlagsUpdated = false;
    if (feedsNeedingLastPageFlag.length > 0) {
      log.info("[WARMUP] Updating feed flags", {
        feedCount: feedsNeedingLastPageFlag.length,
      });
      await Promise.all(
        feedsNeedingLastPageFlag.map((feedId) =>
          db
            .update(feed)
            .set({ loadedLastPage: true } as Partial<Feed>)
            .where(eq(feed.id, feedId))
            .execute(),
        ),
      );
      feedFlagsUpdated = true;
    }

    // Emit item and feed table updates once after both operations
    if (allItems.length > 0 || feedFlagsUpdated) {
      const tablesToEmit = [];
      if (allItems.length > 0) tablesToEmit.push("item");
      if (feedFlagsUpdated) tablesToEmit.push("feed");
      emitTableUpdates(tablesToEmit);
    }

    // Download content if enabled
    if (allItems.length > 0) {
      const allItemIds = allItems.map((item) => item.id);

      // Query which items already have content loaded using unnest CTE for performance
      const itemIdsSub = db
        .$with("itemIdsSub", {
          id: item.id,
        })
        .as(sql`SELECT unnest(${sql.placeholder("itemIds")}::text[]) as "id"`);

      const itemsWithContent = await db
        .with(itemIdsSub)
        .select({ id: item.id })
        .from(itemIdsSub)
        .innerJoin(item, and(eq(item.id, itemIdsSub.id), eq(item.loadedContent, true)))
        .execute({ itemIds: allItemIds });

      const loadedItemIds = new Set(itemsWithContent.map((i) => i.id));
      const itemIdsNeedingContent = allItemIds.filter((id) => !loadedItemIds.has(id));

      log.info("[WARMUP] Filtered items by content status", {
        totalItems: allItemIds.length,
        alreadyLoaded: loadedItemIds.size,
        needsDownload: itemIdsNeedingContent.length,
      });

      if (itemIdsNeedingContent.length === 0) {
        log.info("[WARMUP] All items already have content loaded, skipping download");
        stats.contentDownloaded = 0;
        stats.durationMs = Date.now() - startTime;

        if (finishAction) {
          finishAction(actions.warmupFull(), stats);
        }

        return stats;
      }

      log.info("[WARMUP] Downloading content for items without content", {
        itemCount: itemIdsNeedingContent.length,
        itemIds: itemIdsNeedingContent,
      });

      // Track content fetch phase
      if (trackAction) {
        trackAction(actions.warmupFetchContent(), {
          itemCount: itemIdsNeedingContent.length,
        });
      }

      // Download in chunks of 100 items
      const chunkSize = 100;
      const itemIdChunks: string[][] = [];
      for (let i = 0; i < itemIdsNeedingContent.length; i += chunkSize) {
        itemIdChunks.push(itemIdsNeedingContent.slice(i, i + chunkSize));
      }

      log.info(`[WARMUP] Split into ${itemIdChunks.length} chunks of up to ${chunkSize} items`);

      // Fetch all content in parallel
      const contentPromises = itemIdChunks.map((itemChunk, idx) =>
        client.getFeedItemContent(workspaceId, itemChunk, undefined, undefined).then((result) => {
          log.info(`[WARMUP] Content chunk ${idx + 1}/${itemIdChunks.length} downloaded (${itemChunk.length} items)`);
          return result;
        }),
      );

      const contentResults = await Promise.all(contentPromises);

      // Download events separately to avoid duplicate key issues when events span multiple chunks
      log.info("[WARMUP] Downloading events for items needing content");
      const eventPromise = downloadOldestFeedItemEvents(itemIdsNeedingContent, workspaceId, undefined, true);

      // Batch DB operation: Collect all content and write in bulk
      log.info("[WARMUP] Writing all content to database");
      const allTranscriptions = [];
      const allAudioEncodings = [];
      const allFiles = [];
      const allLinks = [];
      const allDisplayArtifacts = [];
      const allCallRecords = [];

      for (const content of contentResults) {
        if (content) {
          allTranscriptions.push(...(content.transcriptions || []));
          allAudioEncodings.push(...(content.audioEncodings || []));
          allFiles.push(...(content.files || []));
          allLinks.push(...(content.links || []));
          allDisplayArtifacts.push(...(content.displayArtifacts || []));
          allCallRecords.push(...(content.callRecords || []));
        }
      }

      log.info("[WARMUP] Content aggregated, writing to database", {
        transcriptions: allTranscriptions.length,
        audioEncodings: allAudioEncodings.length,
        files: allFiles.length,
        links: allLinks.length,
        displayArtifacts: allDisplayArtifacts.length,
        callRecords: allCallRecords.length,
      });

      // Defer all emits until after all content is written
      await Promise.all([
        bulkUpsertWsTranscriptions(allTranscriptions, true),
        bulkUpsertWsAudioEncodings(allAudioEncodings, true),
        bulkUpsertFile(allFiles, true),
        bulkUpsertLink(allLinks, true),
        bulkUpsertDisplayArtifact(allDisplayArtifacts, true),
        bulkUpsertCallRecord(allCallRecords, true),
        markItemsContentLoaded(itemIdsNeedingContent, true),
        eventPromise,
      ]);

      log.info(`[WARMUP] Marked ${itemIdsNeedingContent.length} items as content loaded`);

      // Emit all table updates once after all warmup content is written
      emitTableUpdates([
        "item",
        "transcription",
        "audio_encoding",
        "file",
        "link",
        "display_artifact",
        "call_record",
        "account_event",
      ]);

      stats.contentDownloaded = itemIdsNeedingContent.length;

      // Finish content fetch phase
      if (finishAction) {
        finishAction(actions.warmupFetchContent(), {
          contentDownloaded: itemIdsNeedingContent.length,
          transcriptions: allTranscriptions.length,
          audioEncodings: allAudioEncodings.length,
          files: allFiles.length,
          links: allLinks.length,
          displayArtifacts: allDisplayArtifacts.length,
          callRecords: allCallRecords.length,
        });
      }
    }

    stats.durationMs = Date.now() - startTime;

    log.info("[WARMUP] Completed feed warmup", stats);

    // Finish overall telemetry span
    if (finishAction) {
      finishAction(actions.warmupFull(), stats);
    }

    return stats;
  } catch (error) {
    stats.durationMs = Date.now() - startTime;
    log.error("[WARMUP] Feed warmup failed", { error, stats });

    if (finishAction) {
      finishAction(actions.warmupFull(), { ...stats, error: String(error) });
    }

    return stats;
  }
}
